import { MEASURES, intervalToDisplay, toDisplay, type Interval, type Measure } from '@bioterafi/stats';
import { formatDisplayValue, withMinus } from '@bioterafi/exports';
import { STUDY_DESIGN_NAMES, STUDY_DESIGNS, type DataOrigin, type RobDomain, type StudyDesign, type ValueStatus } from '@bioterafi/evidence-model';
import type { RobJudgement } from '@bioterafi/stats';


/** Sentence-case measure names, from the engine's measure registry (CORE-300). */
export const MEASURE_NAME: Record<Measure, string> = Object.fromEntries(
  Object.values(MEASURES).map((m) => [m.measure, m.name]),
) as Record<Measure, string>;

export const MODEL_NAME = { fixed: 'Fixed effect', random: 'Random effects' } as const;

export const ORIGIN_NAME: Record<DataOrigin['kind'], string> = {
  published_dataset: 'Published dataset',
  hand_entered: 'Hand-entered',
  pipeline: 'Bioterafi pipeline',
};

export const STATUS_NAME: Record<ValueStatus, string> = {
  machine: 'Machine extracted',
  human_confirmed: 'Confirmed',
  human_corrected: 'Corrected',
};

/** One set of design names for the whole app (EVM-171). */
export const STUDY_DESIGN_NAME: Record<StudyDesign, string> = Object.fromEntries(
  STUDY_DESIGNS.map((d) => [d, STUDY_DESIGN_NAMES[d].singular]),
) as Record<StudyDesign, string>;

export const ROB_DOMAIN_NAME: Record<RobDomain, string> = {
  randomisation: 'Randomisation process',
  deviations: 'Deviations from intended interventions',
  missing_data: 'Missing outcome data',
  measurement: 'Measurement of the outcome',
  selection: 'Selection of the reported result',
};

export const ROB_NAME: Record<RobJudgement, string> = {
  low: 'Low risk',
  some_concerns: 'Some concerns',
  high: 'High risk',
};

/** Shared with the Worker's alert messages, so both print the same number (ALRT-034). */
export { formatDisplayValue };

/**
 * An analysis-scale estimate shown on the display scale. `n` is the sample size a Freeman-Tukey
 * value is back-transformed with (PAGE-308): the study's `ni`, or `harmonicMeanN` for a pooled value.
 */
export function formatEffect(measure: Measure, analysisValue: number, n?: number): string {
  return formatDisplayValue(measure, toDisplay(measure, analysisValue, n));
}

/** "0.70 to 0.85": words, not a dash, so negative risk differences read clearly. */
export function formatInterval(measure: Measure, interval: Interval, n?: number): string {
  const d = intervalToDisplay(measure, interval, n);
  return `${formatDisplayValue(measure, d.lower)} to ${formatDisplayValue(measure, d.upper)}`;
}

export function formatP(p: number): string {
  if (!Number.isFinite(p)) return 'not available';
  if (p < 0.001) return '< 0.001';
  if (p < 0.1) return p.toFixed(3);
  return p.toFixed(2);
}

/** "p < 0.001" or "p = 0.042". */
export function formatPWithSymbol(p: number): string {
  const v = formatP(p);
  return v.startsWith('<') ? `p ${v}` : `p = ${v}`;
}

export function formatPercent(value: number, digits = 0): string {
  return `${value.toFixed(digits)}%`;
}

export function formatTau2(value: number): string {
  if (value === 0) return '0';
  if (Math.abs(value) < 0.0001) return '< 0.0001';
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toPrecision(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

export function formatStat(value: number, digits = 2): string {
  return withMinus(value.toFixed(digits));
}

const INTEGER = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

export function formatInteger(value: number): string {
  return INTEGER.format(value);
}

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'not recorded';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DATE.format(d);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatInteger(n)} ${n === 1 ? one : many}`;
}

/** Turns an attribute key such as "incomeLevel" into "Income level". */
export function humanise(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
